import React from 'react'
import { Layout } from '../components/layout/Layout'
import { SectionHeading } from '../components/ui/SectionHeading'
import { Button } from '../components/ui/Button'
import { motion } from 'framer-motion'
import {
  Youtube,
  Facebook,
  BookOpen,
  Headphones,
  Video,
  ExternalLink,
  Play,
  Calendar,
  CheckCircle,
} from 'lucide-react'
const sermons = [
  {
    id: 1,
    title: "Walking in Faith: Trusting God's Plan",
    speaker: 'Pastor',
    date: 'October 2024',
    duration: '45 min',
    thumbnail:
      'https://images.unsplash.com/photo-1504052434569-70ad5836ab65?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
    youtubeId: 'eXE7XywBQ-c',
  },
  {
    id: 2,
    title: 'The Power of Prayer',
    speaker: 'Pastor',
    date: 'September 2024',
    duration: '42 min',
    thumbnail:
      'https://images.unsplash.com/photo-1438232992991-995b7058bbb3?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
    youtubeId: 'eXE7XywBQ-c',
  },
  {
    id: 3,
    title: 'Building Strong Families',
    speaker: 'Pastor',
    date: 'August 2024',
    duration: '50 min',
    thumbnail:
      'https://images.unsplash.com/photo-1511649475669-e288648b2339?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
    youtubeId: 'eXE7XywBQ-c',
  },
]
const platforms = [
  {
    icon: Youtube,
    title: 'Sermon Archive',
    description: 'Watch past sermons and teachings from our services',
    link: 'https://youtube.com/@householdchurch',
    linkText: 'Watch on YouTube',
    color: 'bg-red-500',
  },
  {
    icon: Facebook,
    title: 'Facebook Live',
    description: 'Join our live services and community updates',
    link: 'https://facebook.com/householdchurch',
    linkText: 'Follow on Facebook',
    color: 'bg-blue-600',
  },
  {
    icon: BookOpen,
    title: 'Deep Dive Materials',
    description: 'Weekly study guides and devotional resources for Deep Dive',
    link: '/contact',
    linkText: 'Request Materials',
    color: 'bg-[#0A1F44]',
    internal: true,
  },
  {
    icon: Headphones,
    title: 'Prayer Line',
    description: 'Connect with us for prayer support',
    link: 'tel:301-996-6907',
    linkText: 'Call: (301) 996-6907',
    color: 'bg-[#C8102E]',
  },
]
export function ResourcesPage() {
  return (
    <Layout>
      {/* Hero Section */}
      <div className="bg-[#0A1F44] pt-44 md:pt-48 pb-20 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img
            src="https://images.unsplash.com/photo-1516450360452-9312f5e86fc7?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80"
            alt="Background"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="container-custom relative z-10">
          <motion.div
            initial={{
              opacity: 0,
              y: 20,
            }}
            animate={{
              opacity: 1,
              y: 0,
            }}
            className="max-w-3xl"
          >
            <span className="inline-block text-[#C8102E] text-sm font-semibold tracking-widest uppercase mb-4">
              Equip & Grow
            </span>
            <h1 className="text-4xl md:text-6xl font-serif font-bold mb-6">
              Resources
            </h1>
            <p className="text-xl text-white/80 leading-relaxed">
              Access sermons, teachings, and study materials to help you grow in
              your faith journey and deepen your relationship with God.
            </p>
          </motion.div>
        </div>
      </div>

      {/* Platforms Grid */}
      <section className="py-20 bg-white">
        <div className="container-custom">
          <SectionHeading
            title="Ways to Connect"
            subtitle="Platforms"
            alignment="center"
          />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
            {platforms.map((platform, index) => (
              <motion.div
                key={platform.title}
                initial={{
                  opacity: 0,
                  y: 20,
                }}
                whileInView={{
                  opacity: 1,
                  y: 0,
                }}
                viewport={{
                  once: true,
                }}
                transition={{
                  delay: index * 0.1,
                }}
                className="bg-[#F5F7FA] p-8 rounded-2xl hover:shadow-lg transition-shadow group"
              >
                <div
                  className={`w-14 h-14 ${platform.color} rounded-xl flex items-center justify-center mb-6 text-white group-hover:scale-110 transition-transform`}
                >
                  <platform.icon className="w-7 h-7" />
                </div>
                <h3 className="text-xl font-bold text-[#0A1F44] mb-3">
                  {platform.title}
                </h3>
                <p className="text-gray-600 mb-6 text-sm leading-relaxed">
                  {platform.description}
                </p>
                <a
                  href={platform.link}
                  target={platform.internal ? '_self' : '_blank'}
                  rel={platform.internal ? '' : 'noopener noreferrer'}
                  className="inline-flex items-center text-sm font-bold text-[#0A1F44] hover:text-[#C8102E] transition-colors"
                >
                  {platform.linkText}
                  <ExternalLink className="w-4 h-4 ml-2" />
                </a>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Recent Sermons */}
      <section className="py-20 bg-[#F5F7FA]">
        <div className="container-custom">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-6">
            <SectionHeading
              title="Recent Messages"
              subtitle="Watch & Listen"
              alignment="left"
              className="mb-0"
            />
            <a
              href="https://youtube.com/@householdchurch"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button
                variant="outline"
                rightIcon={<Youtube className="w-4 h-4" />}
              >
                View All on YouTube
              </Button>
            </a>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {sermons.map((sermon, index) => (
              <motion.div
                key={sermon.id}
                initial={{
                  opacity: 0,
                  y: 20,
                }}
                whileInView={{
                  opacity: 1,
                  y: 0,
                }}
                viewport={{
                  once: true,
                }}
                transition={{
                  delay: index * 0.1,
                }}
                className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all group"
              >
                <div className="relative aspect-video overflow-hidden">
                  <img
                    src={sermon.thumbnail}
                    alt={sermon.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="w-16 h-16 bg-[#C8102E] rounded-full flex items-center justify-center text-white">
                      <Play className="w-8 h-8 ml-1" fill="currentColor" />
                    </div>
                  </div>
                  <div className="absolute bottom-3 right-3 bg-black/80 text-white text-xs font-medium px-2 py-1 rounded">
                    {sermon.duration}
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-4 text-xs text-gray-500 mb-3 uppercase tracking-wider font-semibold">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {sermon.date}
                    </span>
                  </div>
                  <h3 className="text-xl font-serif font-bold text-[#0A1F44] mb-2 line-clamp-2 group-hover:text-[#C8102E] transition-colors">
                    {sermon.title}
                  </h3>
                  <p className="text-gray-600 text-sm">{sermon.speaker}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Series/Study */}
      <section className="py-20 bg-[#0A1F44] text-white">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{
                opacity: 0,
                x: -30,
              }}
              whileInView={{
                opacity: 1,
                x: 0,
              }}
              viewport={{
                once: true,
              }}
            >
              <span className="text-[#C8102E] text-sm font-semibold tracking-widest uppercase mb-4 block">
                Featured Study
              </span>
              <h2 className="text-3xl md:text-4xl font-serif font-bold mb-6">
                Deep Dive: Understanding the Kingdom
              </h2>
              <p className="text-white/80 text-lg leading-relaxed mb-8">
                Join us every Sunday at 9:00 AM for our Deep Dive Bible study.
                This interactive session is designed to help you understand
                God's Word on a deeper level and apply it to your daily life.
              </p>
              <div className="space-y-4 mb-8">
                <div className="flex items-center gap-3 text-white/90">
                  <div className="w-8 h-8 bg-white/10 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-4 h-4 text-[#C8102E]" />
                  </div>
                  <span>In-depth scriptural analysis</span>
                </div>
                <div className="flex items-center gap-3 text-white/90">
                  <div className="w-8 h-8 bg-white/10 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-4 h-4 text-[#C8102E]" />
                  </div>
                  <span>Interactive Q&A sessions</span>
                </div>
                <div className="flex items-center gap-3 text-white/90">
                  <div className="w-8 h-8 bg-white/10 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-4 h-4 text-[#C8102E]" />
                  </div>
                  <span>Practical life applications</span>
                </div>
              </div>
              <Button
                variant="primary"
                className="bg-[#C8102E] hover:bg-[#A00D24] border-none"
              >
                Join Next Session
              </Button>
            </motion.div>

            <motion.div
              initial={{
                opacity: 0,
                x: 30,
              }}
              whileInView={{
                opacity: 1,
                x: 0,
              }}
              viewport={{
                once: true,
              }}
              className="max-w-4xl mx-auto"
            >
              <div className="relative rounded-2xl overflow-hidden shadow-2xl bg-black">
                <div className="aspect-video">
                  <iframe
                    src="https://www.youtube.com/embed/eXE7XywBQ-c"
                    title="Featured Sermon"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                    className="w-full h-full"
                  />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-[#0A1F44] border-t border-white/10">
        <div className="container-custom text-center">
          <h2 className="text-3xl font-serif font-bold text-white mb-4">
            Stay Updated
          </h2>
          <p className="text-white/80 mb-8 max-w-xl mx-auto">
            Follow us on social media to get notified when new content is
            available.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="https://youtube.com/@householdchurch"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button
                variant="white"
                leftIcon={<Youtube className="w-5 h-5" />}
              >
                YouTube
              </Button>
            </a>
            <a
              href="https://facebook.com/householdchurch"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button
                variant="white"
                leftIcon={<Facebook className="w-5 h-5" />}
              >
                Facebook
              </Button>
            </a>
          </div>
        </div>
      </section>
    </Layout>
  )
}
function CheckCircle({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
      <polyline points="22 4 12 14.01 9 11.01" />
    </svg>
  )
}
